/**
 * The bearer for a hand-rolled request: the library's session, nothing more.
 *
 * Jacky, 16 October 2026: *"Every time prepare completion report the system logs me out"*. Preparing the
 * report fetches every installer and office photo, and each fetch asked `getToken()` for a bearer. Since
 * 18 September `getToken()` forced `auth.refreshSession()` whenever the token was inside 60 s of expiry —
 * written for a long-open tab holding a dead bearer. That force bypasses everything auth-js does to keep
 * refreshing safe: `getSession()` already refreshes inside its own 90 s margin, single-flighted, with a
 * failure cooldown, and hands back the still-valid session when a proactive refresh is refused. When the
 * library's refresh was held back (discarded by its commit guard, or cooling down), the forced one fired
 * again on every request. The auth log for 21:16–21:27 Sydney shows ~311 `/token` refreshes from the
 * office IP in ten minutes, then `429 over_request_rate_limit`; a refused refresh on an expired token
 * removes the session, and the CRM drops to the sign-in screen — five sign-outs and five sign-ins.
 *
 * So the bearer is whatever `getSession()` returns. Never call `refreshSession()` from here again: the
 * dead-bearer case of 18 September is the library's own margin now (auth-js 2.115), and
 * `session-token.test.ts` fails if a refresh is forced.
 */
export interface SessionSource {
  getSession(): Promise<{data: {session: {access_token?: string | null} | null}}>;
}

export async function sessionToken(auth: SessionSource): Promise<string | null> {
  const {data} = await auth.getSession();
  return data.session?.access_token ?? null;
}
